import { useCallback, useEffect, useRef } from 'react';
import { uuidv7 } from 'uuidv7';
import type { ControlClientMessage } from '@/contracts';
import { logInfo } from '@/lib/logger';
import {
  isZeroVelocity,
  MOTION_DIAGNOSTIC_SAMPLE_RATE,
  shouldLogSample,
} from '@/lib/teleopMotionDiagnostics';
import type {
  MotionCommandReason,
  TeleopMotionConfig,
  Velocity,
} from './teleopMotionTypes';

type UseTeleopMotionControllerParams = {
  sessionId: string;
  robotId: string;
  connected: boolean;
  controlsEnabled: boolean;
  debugWs: boolean;
  config: TeleopMotionConfig;
  sendMessage: (message: ControlClientMessage) => boolean;
  onGrabAttempted?: () => void;
};

type UseTeleopMotionControllerResult = {
  updateDesiredMotion: (
    partial: Partial<Velocity>,
    reason: MotionCommandReason,
  ) => void;
  sendGrab: () => boolean;
};

const nextCommandSeqSeed = () => {
  const now = Date.now();
  const seed = now * 1000;

  return seed;
};

const isSameVelocity = (left: Velocity | null, right: Velocity | null) => {
  if (!left || !right) {
    return false;
  }

  const sameX = left.x === right.x;
  const sameY = left.y === right.y;
  const sameZ = left.z === right.z;
  const isSame = sameX && sameY && sameZ;

  return isSame;
};

const hasUnsentDesiredMotion = (
  lastSentVelocity: Velocity | null,
  desiredVelocity: Velocity,
) => {
  if (lastSentVelocity === null) {
    const desiredVelocityIsZero = isZeroVelocity(desiredVelocity);
    const hasPendingMotion = !desiredVelocityIsZero;

    return hasPendingMotion;
  }

  const desiredVelocityChanged = !isSameVelocity(lastSentVelocity, desiredVelocity);

  return desiredVelocityChanged;
};

const isReleaseReason = (reason: MotionCommandReason | null) => {
  if (reason === null) {
    return false;
  }

  const isJoystickRelease = reason === 'joystick_release';
  const isMovementKeyRelease = reason === 'movement_key_release';
  const isRotationDialRelease = reason === 'rotation_dial_release';
  const isRotationKeyRelease = reason === 'rotation_key_release';
  const isRelease =
    isJoystickRelease ||
    isMovementKeyRelease ||
    isRotationDialRelease ||
    isRotationKeyRelease;

  return isRelease;
};

export const useTeleopMotionController = ({
  sessionId,
  robotId,
  connected,
  controlsEnabled,
  debugWs,
  config,
  sendMessage,
  onGrabAttempted,
}: UseTeleopMotionControllerParams): UseTeleopMotionControllerResult => {
  const desiredVelocityRef = useRef<Velocity>({ x: 0, y: 0, z: 0 });
  const desiredMotionReasonRef = useRef<MotionCommandReason | null>(null);
  const lastSentVelocityRef = useRef<Velocity | null>(null);
  const zeroReleasePendingRef = useRef(false);
  const localCoalescedCountRef = useRef(0);
  const zeroReleaseCountRef = useRef(0);
  const sendLogCountRef = useRef(0);
  const commandSeqRef = useRef(nextCommandSeqSeed());

  const sendMotionIntent = useCallback(
    (velocity: Velocity) => {
      if (!robotId) {
        return false;
      }

      const motionAxes: [number, number, number] = [
        velocity.x,
        velocity.y,
        velocity.z,
      ];
      commandSeqRef.current += 1;
      const nextSequence = commandSeqRef.current;
      const motionIntentMessage: ControlClientMessage = {
        handler: 'gamepad',
        type: 'motion_intent',
        session_id: sessionId,
        robot_id: robotId,
        seq: nextSequence,
        ts_ms: Date.now(),
        horizon_ms: config.motionHorizonMs,
        axes: motionAxes,
      };
      const sent = sendMessage(motionIntentMessage);

      if (debugWs && sendLogCountRef.current < 5) {
        logInfo('teleop.command.sent', 'send motion_intent', {
          axes: motionAxes,
          robotId,
          sent,
        });
        sendLogCountRef.current += 1;
      }

      return sent;
    },
    [config.motionHorizonMs, debugWs, robotId, sendMessage, sessionId]
  );

  const flushMotionIntent = useCallback(() => {
    if (!connected) {
      return;
    }

    if (!controlsEnabled) {
      return;
    }

    if (!robotId) {
      return;
    }

    const desiredVelocity = desiredVelocityRef.current;
    const desiredReason = desiredMotionReasonRef.current;
    const lastSentVelocity = lastSentVelocityRef.current;
    const desiredVelocityIsZero = isZeroVelocity(desiredVelocity);
    const shouldRenewActiveMotion = !desiredVelocityIsZero;
    const shouldRetryZeroRelease =
      desiredVelocityIsZero &&
      zeroReleasePendingRef.current;
    const shouldSendChangedZero =
      desiredVelocityIsZero &&
      lastSentVelocity !== null &&
      !isSameVelocity(lastSentVelocity, desiredVelocity);
    const shouldSendMotion =
      shouldRenewActiveMotion ||
      shouldRetryZeroRelease ||
      shouldSendChangedZero;

    if (!shouldSendMotion) {
      return;
    }

    const sent = sendMotionIntent(desiredVelocity);

    if (!sent) {
      return;
    }

    lastSentVelocityRef.current = desiredVelocity;

    if (!desiredVelocityIsZero) {
      return;
    }

    zeroReleasePendingRef.current = false;
    const sentFromRelease = isReleaseReason(desiredReason);

    if (!sentFromRelease) {
      return;
    }

    zeroReleaseCountRef.current += 1;
    const zeroReleaseCount = zeroReleaseCountRef.current;
    const shouldLogZeroRelease = shouldLogSample(
      zeroReleaseCount,
      MOTION_DIAGNOSTIC_SAMPLE_RATE,
    );

    if (!shouldLogZeroRelease) {
      return;
    }

    logInfo('teleop.motion.release_sent', 'sent zero-intent release', {
      axes: [desiredVelocity.x, desiredVelocity.y, desiredVelocity.z],
      reason: desiredReason,
      robotId,
      sessionId,
      sample_rate: MOTION_DIAGNOSTIC_SAMPLE_RATE,
    });
  }, [connected, controlsEnabled, robotId, sendMotionIntent, sessionId]);

  const updateDesiredMotion = useCallback(
    (partial: Partial<Velocity>, reason: MotionCommandReason) => {
      const previousVelocity = desiredVelocityRef.current;
      const previousReason = desiredMotionReasonRef.current;
      const nextVelocity: Velocity = {
        ...previousVelocity,
        ...partial,
      };
      const hasPendingVelocity = hasUnsentDesiredMotion(
        lastSentVelocityRef.current,
        previousVelocity,
      );
      const nextVelocityIsDifferent = !isSameVelocity(
        previousVelocity,
        nextVelocity,
      );
      const replacesPendingVelocity =
        hasPendingVelocity &&
        nextVelocityIsDifferent;

      if (replacesPendingVelocity) {
        localCoalescedCountRef.current += 1;
        const localCoalescedCount = localCoalescedCountRef.current;
        const shouldLogCoalesced = shouldLogSample(
          localCoalescedCount,
          MOTION_DIAGNOSTIC_SAMPLE_RATE,
        );

        if (shouldLogCoalesced) {
          logInfo('teleop.motion.coalesced', 'coalesced pending motion locally', {
            next_axes: [nextVelocity.x, nextVelocity.y, nextVelocity.z],
            next_reason: reason,
            previous_axes: [
              previousVelocity.x,
              previousVelocity.y,
              previousVelocity.z,
            ],
            previous_reason: previousReason,
            robotId,
            sessionId,
            sample_rate: MOTION_DIAGNOSTIC_SAMPLE_RATE,
          });
        }
      }

      desiredVelocityRef.current = nextVelocity;
      desiredMotionReasonRef.current = reason;
      const isPendingRelease =
        isReleaseReason(reason) &&
        isZeroVelocity(nextVelocity);
      zeroReleasePendingRef.current = isPendingRelease;
    },
    [robotId, sessionId]
  );

  const sendGrab = useCallback(() => {
    if (!robotId) {
      return false;
    }

    if (!connected) {
      return false;
    }

    const currentVelocity = desiredVelocityRef.current;
    const grabMessageId = uuidv7();
    const grabMessage: ControlClientMessage = {
      handler: 'gamepad',
      type: 'grab',
      session_id: sessionId,
      robot_id: robotId,
      message_id: grabMessageId,
      idempotency_key: `grab:${sessionId}:${grabMessageId}`,
    };
    const sent = sendMessage(grabMessage);

    if (debugWs) {
      logInfo('teleop.command.sent', 'send grab', {
        axes: [currentVelocity.x, currentVelocity.y, currentVelocity.z],
        robotId,
        sent,
      });
    }

    const handleGrabAttempted = onGrabAttempted;

    if (handleGrabAttempted) {
      handleGrabAttempted();
    }

    return sent;
  }, [
    connected,
    debugWs,
    onGrabAttempted,
    robotId,
    sendMessage,
    sessionId,
  ]);

  useEffect(() => {
    const zeroVelocity = { x: 0, y: 0, z: 0 };

    desiredVelocityRef.current = zeroVelocity;
    desiredMotionReasonRef.current = null;
    zeroReleasePendingRef.current = false;
    lastSentVelocityRef.current = null;
    commandSeqRef.current = nextCommandSeqSeed();
  }, [robotId, sessionId]);

  useEffect(() => {
    if (!connected) {
      return;
    }

    if (!controlsEnabled) {
      return;
    }

    if (!robotId) {
      return;
    }

    flushMotionIntent();
    const motionInterval = window.setInterval(() => {
      flushMotionIntent();
    }, config.motionSendIntervalMs);

    return () => {
      window.clearInterval(motionInterval);
    };
  }, [
    config.motionSendIntervalMs,
    connected,
    controlsEnabled,
    flushMotionIntent,
    robotId,
  ]);

  useEffect(() => {
    return () => {
      desiredMotionReasonRef.current = null;
      zeroReleasePendingRef.current = false;
    };
  }, []);

  return {
    updateDesiredMotion,
    sendGrab,
  };
};
